import React, { useEffect, useMemo, useRef, useState } from "react";
import { toast } from "sonner";
import { Search, RotateCcw, Plus, MoreVertical, Copy, Calendar, ChevronLeft, ChevronRight, ChevronDown, Users, Activity, AlertTriangle, BarChart3 } from "lucide-react";
import "./students-page.css";

// The students table of the school pages (My Students for a teacher, Students for a principal), laid out as a dashboard: four summary
// cards, one toolbar of working filters, a table with progress bars, three-dot actions and real pagination. All data is the existing
// /school/students list (already limited to the teacher's classes or the principal's school); nothing here fetches or invents anything.

const WEEK_MS = 7 * 86400000;
const initials = (name) => (name || "?").split(/\s+/).filter(Boolean).slice(0, 2).map((w) => w[0].toUpperCase()).join("");
const tone = (pct) => (pct <= 0 ? "none" : pct < 40 ? "low" : pct < 70 ? "mid" : "high");
const classOf = (s) => `${s.class_name} ${s.section}`.trim();
const pctOf = (s) => (s.reported ? Number(s.percent) || 0 : 0);

function lastActive(iso) {
  if (!iso) return { text: "Never", kind: "never" };
  const t = new Date(iso); const days = Math.floor((Date.now() - t.getTime()) / 86400000);
  if (days <= 0) return { text: "Today", kind: "recent" };
  if (days === 1) return { text: "Yesterday", kind: "recent" };
  return { text: t.toLocaleDateString(undefined, { day: "numeric", month: "short", year: "numeric" }), kind: Date.now() - t.getTime() <= WEEK_MS ? "recent" : "old" };
}

const BANDS = [["", "All Progress"], ["none", "Not started"], ["1-25", "1 – 25%"], ["26-50", "26 – 50%"], ["51-75", "51 – 75%"], ["76-100", "76 – 100%"]];
const inBand = (pct, b) => !b || (b === "none" ? pct <= 0 : b === "1-25" ? pct > 0 && pct <= 25 : b === "26-50" ? pct > 25 && pct <= 50 : b === "51-75" ? pct > 50 && pct <= 75 : pct > 75);

function Pages({ page, pages, go }) {
  const out = []; const add = (n) => out.push(n);
  if (pages <= 7) for (let i = 1; i <= pages; i++) add(i);
  else { add(1); if (page > 3) add("…"); for (let i = Math.max(2, page - 1); i <= Math.min(pages - 1, page + 1); i++) add(i); if (page < pages - 2) add("…r"); add(pages); }
  return out.map((n, i) => (typeof n === "number"
    ? <button key={n} type="button" className={`stl-page${n === page ? " on" : ""}`} aria-current={n === page ? "page" : undefined} onClick={() => go(n)}>{n}</button>
    : <span key={`${n}${i}`} className="stl-dots" aria-hidden="true">…</span>));
}

function RowMenu({ s, onOpen, onReset, onRemove }) {
  const [open, setOpen] = useState(false);
  const ref = useRef(null);
  useEffect(() => {
    if (!open) return undefined;
    const close = (e) => { if (ref.current && !ref.current.contains(e.target)) setOpen(false); };
    const esc = (e) => { if (e.key === "Escape") setOpen(false); };
    document.addEventListener("mousedown", close); document.addEventListener("keydown", esc);
    return () => { document.removeEventListener("mousedown", close); document.removeEventListener("keydown", esc); };
  }, [open]);
  return (
    <span className="stl-menu" ref={ref} onClick={(e) => e.stopPropagation()}>
      <button type="button" className="stl-dots-btn" aria-label={`Actions for ${s.name}`} aria-haspopup="menu" aria-expanded={open} onClick={() => setOpen((v) => !v)} data-testid={`student-menu-${s.user_id}`}><MoreVertical size={18} /></button>
      {open && (
        <div className="stl-pop" role="menu">
          <button type="button" role="menuitem" onClick={() => { setOpen(false); onOpen(s); }}>View details</button>
          {!s.record && <button type="button" role="menuitem" onClick={() => { setOpen(false); onReset(s); }}>Reset password</button>}
          {!s.record && <button type="button" role="menuitem" className="danger" onClick={() => { setOpen(false); onRemove(s); }}>Remove student</button>}
        </div>)}
    </span>
  );
}

export default function StudentsList({ list, role, teachers, teacherClasses, tch, setTch, me, canAdd, addTitle, onAdd, onOpen, onReset, onRemove, ageBadge }) {
  const [q, setQ] = useState("");
  const [cls, setCls] = useState("");
  const [band, setBand] = useState("");
  const [act, setAct] = useState("");
  const [perPage, setPerPage] = useState(10);
  const [page, setPage] = useState(1);
  const [openCard, setOpenCard] = useState(null);

  const scope = useMemo(() => list.filter((s) => !teacherClasses || teacherClasses.has(classOf(s))), [list, teacherClasses]);
  const classes = useMemo(() => [...new Set(scope.map(classOf))].sort(), [scope]);
  const total = scope.length;
  const started = scope.filter((s) => pctOf(s) > 0).length;
  const avg = total ? Math.round((scope.reduce((n, s) => n + pctOf(s), 0) / total) * 10) / 10 : 0;

  const rows = useMemo(() => scope.filter((s) => {
    const p = pctOf(s); const la = lastActive(s.last_active_at);
    const text = q.trim().toLowerCase();
    return (!text || s.name.toLowerCase().includes(text) || (s.username || "").toLowerCase().includes(text) || (s.link_code || "").toLowerCase().includes(text))
      && (!cls || classOf(s) === cls) && inBand(p, band)
      && (!act || (act === "week" ? la.kind === "recent" : act === "inactive" ? la.kind === "old" : la.kind === "never"));
  }), [scope, q, cls, band, act]);
  useEffect(() => { setPage(1); }, [q, cls, band, act, perPage, tch]);
  useEffect(() => { if (cls && !classes.includes(cls)) setCls(""); }, [classes, cls]);

  const pages = Math.max(1, Math.ceil(rows.length / perPage));
  const cur = Math.min(page, pages);
  const slice = rows.slice((cur - 1) * perPage, cur * perPage);
  const filtered = !!(q || cls || band || act || tch);
  const clear = () => { setQ(""); setCls(""); setBand(""); setAct(""); if (tch && setTch) setTch(""); };
  const copy = (e, text) => { e.stopPropagation(); navigator.clipboard.writeText(text).then(() => toast.success("Copied")).catch(() => toast.error("Copy failed")); };
  const code = (s) => (s.record ? s.link_code : s.username) || "";

  const cards = [
    { icon: Users, tone: "mint", value: total, label: "Total Students" },
    { icon: Activity, tone: "teal", value: started, label: "Active Students" },
    { icon: AlertTriangle, tone: "gold", value: total - started, label: "Not Started" },
    { icon: BarChart3, tone: "green", value: `${avg}%`, label: "Average Progress" },
  ];
  const from = rows.length ? (cur - 1) * perPage + 1 : 0;
  const to = Math.min(cur * perPage, rows.length);

  return (
    <div className="stpage" data-testid="students-tab">
      <header className="stl-head">
        <div style={{ position: "relative", zIndex: 1 }}>
          <h1 className="stl-title">{role === "principal" ? "Students" : "My Students"}</h1>
          <p className="stl-sub">{role === "principal" ? "Monitor every student's learning progress across the school." : "Manage and monitor your students' learning progress."}</p>
        </div>
        <div className="stl-deco" aria-hidden="true">
          <img className="leaf l" src={`${process.env.PUBLIC_URL}/dashboard/leaves-left.png`} alt="" />
          <img className="mosque" src={`${process.env.PUBLIC_URL}/dashboard/mosque-header.png`} alt="" />
          <img className="leaf r" src={`${process.env.PUBLIC_URL}/dashboard/leaves-right.png`} alt="" />
        </div>
      </header>

      <section className="stl-cards" aria-label="Student summary" data-testid="students-summary">
        {cards.map((c) => (
          <div className="stl-card stl-stat" key={c.label}>
            <span className={`stl-ico ${c.tone}`}><c.icon size={28} strokeWidth={1.7} /></span>
            <div><b>{c.value}</b><span>{c.label}</span></div>
          </div>))}
      </section>

      <section className="stl-card stl-tools" aria-label="Filters">
        <label className="stl-search"><Search size={18} aria-hidden="true" /><input value={q} onChange={(e) => setQ(e.target.value)} placeholder="Search name or username" aria-label="Search students" data-testid="students-search" /></label>
        {role === "principal" && teachers.length > 0 && (
          <span className="stl-sel"><select value={tch} onChange={(e) => { setTch(e.target.value); setCls(""); }} data-testid="students-teacher-filter" aria-label="Teacher"><option value="">All teachers</option>{teachers.map((t) => <option key={t.user_id} value={t.user_id}>{t.name}</option>)}</select><ChevronDown size={16} aria-hidden="true" /></span>)}
        <span className="stl-sel"><select value={cls} onChange={(e) => setCls(e.target.value)} data-testid="students-class-filter" aria-label="Class"><option value="">All Classes</option>{classes.map((c) => <option key={c} value={c}>{c}</option>)}</select><ChevronDown size={16} aria-hidden="true" /></span>
        <span className="stl-sel"><select value={band} onChange={(e) => setBand(e.target.value)} data-testid="students-progress-filter" aria-label="Progress">{BANDS.map(([v, l]) => <option key={v} value={v}>{l}</option>)}</select><ChevronDown size={16} aria-hidden="true" /></span>
        <span className="stl-sel"><select value={act} onChange={(e) => setAct(e.target.value)} data-testid="students-activity-filter" aria-label="Activity"><option value="">All Activity</option><option value="week">Active this week</option><option value="inactive">Not active this week</option><option value="never">Never active</option></select><ChevronDown size={16} aria-hidden="true" /></span>
        <button type="button" className="stl-clear" onClick={clear} disabled={!filtered} data-testid="students-clear"><RotateCcw size={16} /> Clear Filters</button>
        <button type="button" className="stl-add" data-testid="add-students" disabled={!canAdd} title={addTitle} onClick={onAdd}><Plus size={18} /> Add Students</button>
      </section>

      <section className="stl-card stl-table-card">
        <div className="stl-table-wrap">
          <table className="stl-table" data-testid="students-table">
            <thead><tr><th>Student</th><th>Username / Link Code</th><th>Class</th><th>Progress</th><th>Last Active</th><th className="r">Actions</th></tr></thead>
            <tbody>
              {slice.map((s) => {
                const p = pctOf(s); const la = lastActive(s.last_active_at);
                return (
                  <tr key={s.user_id} className={s.record ? "" : "click"} onClick={() => { if (!s.record) onOpen(s); }} data-testid={`student-row-${s.user_id}`}>
                    <td><span className="stl-name"><i className="stl-av">{initials(s.name)}</i><b>{s.name}</b>{ageBadge && ageBadge(s)}</span></td>
                    <td><span className="stl-code" data-testid={`link-code-${s.user_id}`}>{code(s)}<button type="button" aria-label="Copy" title="Copy" onClick={(e) => copy(e, code(s))}><Copy size={15} /></button></span></td>
                    <td>{classOf(s)}</td>
                    <td><span className="stl-prog"><span className="stl-bar"><i className={tone(p)} style={{ width: `${Math.min(100, p)}%` }} /></span><em>{s.reported ? `${p}%` : "0%"}</em></span></td>
                    <td><span className={`stl-last ${la.kind}`}><Calendar size={15} aria-hidden="true" />{la.text}</span></td>
                    <td className="r"><RowMenu s={s} onOpen={onOpen} onReset={onReset} onRemove={onRemove} /></td>
                  </tr>);
              })}
              {!slice.length && <tr><td colSpan={6} className="stl-none">{list.length ? "No students match these filters." : "No students yet."}</td></tr>}
            </tbody>
          </table>
        </div>

        <div className="stl-cardlist" data-testid="students-cards">
          {slice.map((s) => {
            const p = pctOf(s); const la = lastActive(s.last_active_at); const open = openCard === s.user_id;
            return (
              <article key={s.user_id} className="stl-sc">
                <button type="button" className="stl-sc-top" onClick={() => setOpenCard(open ? null : s.user_id)} aria-expanded={open}>
                  <i className="stl-av">{initials(s.name)}</i>
                  <span className="stl-sc-main"><b>{s.name}</b><small>{classOf(s)}</small></span>
                  <span className="stl-prog sm"><span className="stl-bar"><i className={tone(p)} style={{ width: `${Math.min(100, p)}%` }} /></span><em>{s.reported ? `${p}%` : "0%"}</em></span>
                  <ChevronDown size={18} className={open ? "turn" : ""} aria-hidden="true" />
                </button>
                {open && (
                  <div className="stl-sc-more">
                    <div><span>Username / link code</span><span className="stl-code">{code(s)}<button type="button" aria-label="Copy" onClick={(e) => copy(e, code(s))}><Copy size={15} /></button></span></div>
                    <div><span>Last active</span><span className={`stl-last ${la.kind}`}><Calendar size={15} />{la.text}</span></div>
                    <div className="stl-sc-actions">
                      <button type="button" onClick={() => onOpen(s)}>View details</button>
                      {!s.record && <button type="button" onClick={() => onReset(s)}>Reset password</button>}
                      {!s.record && <button type="button" className="danger" onClick={() => onRemove(s)}>Remove</button>}
                    </div>
                  </div>)}
              </article>);
          })}
          {!slice.length && <p className="stl-none">{list.length ? "No students match these filters." : "No students yet."}</p>}
        </div>

        <footer className="stl-foot">
          <span className="stl-showing" data-testid="students-showing">Showing {from}–{to} of {rows.length} student{rows.length === 1 ? "" : "s"}</span>
          <span className="stl-rpp">Rows per page: <span className="stl-sel sm"><select value={perPage} onChange={(e) => setPerPage(Number(e.target.value))} aria-label="Rows per page" data-testid="students-rpp">{[10, 25, 50, 100].map((n) => <option key={n} value={n}>{n}</option>)}</select><ChevronDown size={14} aria-hidden="true" /></span></span>
          <nav className="stl-pager" aria-label="Pages">
            <button type="button" className="stl-pn" onClick={() => setPage(cur - 1)} disabled={cur <= 1} data-testid="students-prev"><ChevronLeft size={16} /> Previous</button>
            <Pages page={cur} pages={pages} go={setPage} />
            <button type="button" className="stl-pn" onClick={() => setPage(cur + 1)} disabled={cur >= pages} data-testid="students-next">Next <ChevronRight size={16} /></button>
          </nav>
        </footer>
      </section>
    </div>
  );
}
